import {
  PRIMARY_SURVEY_ID_SYSTEM,
  QuestionnaireResponseAttachment,
} from '../Attachment';
import React, { useEffect } from 'react';
import { useNotify } from '../../../../../../hooks';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Survey } from '../../../../../Surveys';
import _noop from 'lodash/noop';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { useModalStyles } from './styles';
import { defineMessages, useIntl } from 'react-intl';
import useFhirSearch, {
  Hits,
} from '../../../../../Surveys/util/FhirSearch/useFhirSearch';

const messages = defineMessages({
  surveyError: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.surveyViewModal.surveyError',
    defaultMessage: 'Error loading survey',
  },
});

type SurveyViewProps = {
  attachment: QuestionnaireResponseAttachment;
  onDismiss: () => void;
};

export const SurveyViewModal = (props: SurveyViewProps) => {
  const { attachment, onDismiss } = props;
  const classes = useModalStyles();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();

  const [response, isLoading] = useFhirSearch<
    Hits<fhir.QuestionnaireResponse | fhir.Questionnaire>
  >(
    {
      type: 'select',
      columns: '*',
      from: [{ table: 'questionnaire' }, { table: 'questionnaire_response' }],
      limit: [
        { type: 'number', value: 0 },
        { type: 'number', value: 2 },
      ],
      where: {
        type: 'elasticsearch',
        query: {
          bool: {
            should: [
              {
                bool: {
                  must: [
                    {
                      term: {
                        'resourceType.keyword': 'Questionnaire',
                      },
                    },
                    {
                      term: {
                        'meta.tag.system.keyword': PRIMARY_SURVEY_ID_SYSTEM,
                      },
                    },
                    {
                      term: {
                        'meta.tag.code.keyword': attachment.primarySurveyId,
                      },
                    },
                  ],
                },
              },
              {
                bool: {
                  must: [
                    {
                      term: {
                        'resourceType.keyword': 'QuestionnaireResponse',
                      },
                    },
                    {
                      term: {
                        'id.keyword': attachment.resource.id,
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    },
    {
      // We need to search the entire project to find the Questionnaire and QuestionnaireResponses
      // because Questionnaires are not tied to a specific patient (/patients/:id) or to a specific
      // cohort (/cohorts/:id).
      searchEntireProject: true,
    },
  );

  const questionnaire = response?.hits.hits.find(
    hit => hit._source.resourceType === 'Questionnaire',
  )?._source as fhir.Questionnaire;
  const questionnaireResponse = response?.hits.hits.find(
    hit => hit._source.resourceType === 'QuestionnaireResponse',
  )?._source as fhir.QuestionnaireResponse;

  const hasError = !isLoading && (!questionnaire || !questionnaireResponse);

  useEffect(() => {
    if (hasError) {
      notify(formatMessage(messages.surveyError), 'error');
      onDismiss();
    }
  }, [hasError, notify, formatMessage, onDismiss]);

  return (
    <Modal
      isFullScreen
      isOpen={true}
      onDismiss={onDismiss}
      contentClassName={classes.modalContent}
      data-testid="survey-view-modal"
    >
      {isLoading ? (
        <Box fullWidth fullHeight justify="center" align="center">
          <DotLoader />
        </Box>
      ) : (
        <Box paddingTop={2}>
          {!!questionnaire && !!questionnaireResponse && (
            <Survey
              isReadOnly
              onComplete={_noop}
              onResponseUpdate={_noop}
              questionnaire={questionnaire}
              questionnaireResponse={questionnaireResponse}
            />
          )}
        </Box>
      )}
    </Modal>
  );
};
